/**
 * QuickNotePanel — Peek panel for capturing a quick thought.
 *
 * The flow is: type the note first, then optionally attach it to a
 * page or pick its kind. The default action is "Save note" which
 * creates a freestanding page (so a Quick Note stays a Quick Note
 * — the user doesn't have to know whether it's a page or a database).
 *
 * 'Attach to' lets the user park the note under an existing page.
 * 'Kind' (Page | Database) is only needed if the user wants to
 * decide up front.
 */
import React, { useState, useMemo } from 'react';
import { Box, Group, Text, Textarea, Button, Stack, ActionIcon, Select, Divider, SegmentedControl } from '@mantine/core';
import { useWorkspace } from '../../../core/workspace/WorkspaceContext';
import { usePeek } from './PeekManager';
import { useFocusMode } from '../cockpit/FocusMode/FocusModeProvider';

export const QuickNotePanel: React.FC = () => {
  const [note, setNote] = useState('');
  const [parentId, setParentId] = useState<string | null>(null);
  const [kind, setKind] = useState<'page' | 'database'>('page');
  const { pages, createPage, selectPage } = useWorkspace();
  const { closePeek } = usePeek();
  // F 33 — Focus_Mode: register this peek as a focused 'card' element when it mounts.
  // The provider tracks a stack; this peek registers itself, peers (full-screen page
  // editor, property zoom) can co-register and the topmost entry wins for visual treatment.
  const focusMode = useFocusMode();
  React.useEffect(() => {
    focusMode.enter('quick-note', 'card');
    return () => focusMode.exit('quick-note');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Build a flat list of pages (id → title) for the parent picker
  const parentOptions = useMemo(() => {
    return pages
      .filter(p => !p.parentId) // top-level only for simplicity
      .map(p => ({ value: p.id, label: p.title || 'Untitled' }));
  }, [pages]);

  const handleSave = async () => {
    const title = note.trim();
    if (!title) return;
    try {
      const newPage = await createPage(title, parentId || undefined, kind);
      await selectPage(newPage.id);
      setNote('');
      setParentId(null);
      setKind('page');
      closePeek();
    } catch (e) { console.error('Failed to create note:', e); }
  };

  return (
    <Box className="fc-focus-mode-active" style={{ position: 'fixed', top: 'var(--fc-header-height, 50px)', right: 0, width: 320, height: 'calc(100vh - var(--fc-header-height, 50px))', backgroundColor: 'var(--fc-bg-primary)', boxShadow: '-2px 0 12px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', zIndex: 100 }}>
      <Box p="md" style={{ borderBottom: '1px solid var(--fc-border-primary)' }}>
        <Group justify="space-between" align="center">
          <Text fw={600}>Quick Note</Text>
          <ActionIcon variant="subtle" size="sm" onClick={() => closePeek()}>✕</ActionIcon>
        </Group>
      </Box>
      <Box p="md" style={{ flex: 1, overflowY: 'auto' }}>
        <Textarea
          placeholder="Capture a thought, task, link, or note…"
          size="sm"
          mb="md"
          value={note}
          onChange={e => setNote(e.currentTarget.value)}
          onKeyDown={e => {
            // Cmd/Ctrl+Enter saves, plain Enter inserts newline
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSave();
            }
          }}
          autosize
          minRows={6}
          maxRows={14}
          autoFocus
          styles={{ input: { fontSize: 14, lineHeight: 1.5 } }}
        />

        <Divider my="sm" label="Optional" labelPosition="left" />

        {/* Attach to — pick which page this note lives under */}
        <Box mb="sm">
          <Text size="xs" c="dimmed" mb={4}>Attach to</Text>
          <Select
            placeholder="No attachment (top-level note)"
            data={parentOptions}
            value={parentId}
            onChange={setParentId}
            clearable
            searchable
            size="sm"
            nothingFoundMessage="No pages yet"
          />
        </Box>

        {/* Kind — only needed if the user wants to decide up front */}
        <Box>
          <Text size="xs" c="dimmed" mb={4}>Kind</Text>
          <SegmentedControl
            value={kind}
            onChange={v => setKind(v as 'page' | 'database')}
            data={[
              { value: 'page', label: 'Page' },
              { value: 'database', label: 'Database' },
            ]}
            size="xs"
            fullWidth
          />
          <Text size="xs" c="dimmed" mt={4}>
            Pages hold prose. Databases hold structured rows (gallery, kanban, calendar).
          </Text>
        </Box>
      </Box>
      <Box p="md" style={{ borderTop: '1px solid var(--fc-border-primary)' }}>
        <Button variant="filled" color="blue" fullWidth onClick={handleSave} disabled={!note.trim()}>
          Save note
        </Button>
        <Text size="xs" c="dimmed" ta="center" mt={6}>
          {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'} + Enter
        </Text>
      </Box>
    </Box>
  );
};